import React, { useState, useRef, useEffect, FormEvent, useCallback } from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import styles from '../styles/Login.module.css';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { STRING_CONSTANTS, ERROR_MESSAGES, LANDING_STRINGS } from '../constants/stringConstants';
import { REGISTRATION_CONFIG } from '../constants/dataConstants';
import { AuthView, FormFieldType, Gender } from '../types/enums';
import { RegisterCredentials, RegistrationErrors } from '../types/Auth.types';

const genderToApi = (gender: string): string => gender.toUpperCase();

interface LoginProps {
  initialView?: AuthView;
  onBackToLanding?: () => void;
  intentRole?: 'DOCTOR' | 'PATIENT';
}

const Login: React.FC<LoginProps> = ({ initialView = AuthView.SIGN_IN, onBackToLanding, intentRole }) => {
  const { login, register, googleLogin, completeProfile, isLoading } = useAuth();
  const { isDark } = useTheme();

  // Google button width measurement
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(400);

  // View state
  const [authView, setAuthView] = useState<AuthView>(initialView);

  // Sign-in form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginErrors, setLoginErrors] = useState<{ email?: string; password?: string; general?: string }>({});

  // Registration form state
  const [regForm, setRegForm] = useState<RegisterCredentials>({
    name: '', email: '', password: '', confirmPassword: '',
    gender: '', phone: '', role: intentRole || 'DOCTOR', specialization: '', licenseNumber: '',
  });
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regErrors, setRegErrors] = useState<RegistrationErrors>({});

  // Google OAuth profile completion state
  const [googleTempUserId, setGoogleTempUserId] = useState<string>('');
  const [googleUserName, setGoogleUserName] = useState('');
  const [googleUserEmail, setGoogleUserEmail] = useState('');
  const [profileForm, setProfileForm] = useState({
    gender: '' as Gender | '',
    phone: '',
    specialization: '',
    licenseNumber: '',
  });
  const [profileErrors, setProfileErrors] = useState<RegistrationErrors>({});

  useEffect(() => {
    if (!googleBtnRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const width = Math.floor(entries[0].contentRect.width);
      if (width > 0) setGoogleBtnWidth(width);
    });
    observer.observe(googleBtnRef.current);
    return () => observer.disconnect();
  }, []);

  const validateEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  // ============================================
  // SIGN IN
  // ============================================

  const validateLoginForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email.trim()) newErrors.email = ERROR_MESSAGES.REQUIRED_FIELD;
    else if (!validateEmail(email)) newErrors.email = ERROR_MESSAGES.INVALID_EMAIL;
    if (!password) newErrors.password = ERROR_MESSAGES.REQUIRED_FIELD;
    else if (password.length < REGISTRATION_CONFIG.PASSWORD_MIN_LENGTH) newErrors.password = ERROR_MESSAGES.PASSWORD_TOO_SHORT;
    setLoginErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoginErrors((prev) => ({ ...prev, general: undefined }));
    if (!validateLoginForm()) return;
    try {
      // Trim email to defend against accidental whitespace from autofill /
      // copy-paste — backend also normalizes, but matching it client-side
      // gives a clean retry on failure.
      await login({ email: email.trim(), password });
    } catch (error) {
      setLoginErrors((prev) => ({
        ...prev,
        general: error instanceof Error ? error.message : ERROR_MESSAGES.LOGIN_FAILED,
      }));
    }
  };

  // ============================================
  // REGISTRATION
  // ============================================

  const updateRegField = useCallback((field: keyof RegisterCredentials, value: string) => {
    setRegForm((prev) => ({ ...prev, [field]: value }));
    if (regErrors[field]) setRegErrors((prev) => ({ ...prev, [field]: undefined }));
  }, [regErrors]);

  const getRegistrationRole = (): 'DOCTOR' | 'PATIENT' => intentRole || regForm.role || 'DOCTOR';

  const validateRegForm = (): boolean => {
    const errs: RegistrationErrors = {};
    const registrationRole = getRegistrationRole();
    if (!regForm.name.trim() || regForm.name.trim().length < REGISTRATION_CONFIG.NAME_MIN_LENGTH) errs.name = ERROR_MESSAGES.NAME_TOO_SHORT;
    if (!regForm.email.trim()) errs.email = ERROR_MESSAGES.REQUIRED_FIELD;
    else if (!validateEmail(regForm.email)) errs.email = ERROR_MESSAGES.INVALID_EMAIL;
    if (!regForm.password) errs.password = ERROR_MESSAGES.REQUIRED_FIELD;
    else if (regForm.password.length < REGISTRATION_CONFIG.PASSWORD_MIN_LENGTH) errs.password = ERROR_MESSAGES.PASSWORD_TOO_SHORT;
    if (!regForm.confirmPassword) errs.confirmPassword = ERROR_MESSAGES.REQUIRED_FIELD;
    else if (regForm.confirmPassword !== regForm.password) errs.confirmPassword = ERROR_MESSAGES.PASSWORDS_DO_NOT_MATCH;
    if (!regForm.gender) errs.gender = ERROR_MESSAGES.GENDER_REQUIRED;
    if (registrationRole === 'DOCTOR' && !regForm.specialization) errs.specialization = ERROR_MESSAGES.SPECIALIZATION_REQUIRED;
    if (registrationRole === 'DOCTOR' && (!regForm.licenseNumber || regForm.licenseNumber.length < REGISTRATION_CONFIG.LICENSE_MIN_LENGTH)) errs.licenseNumber = ERROR_MESSAGES.LICENSE_REQUIRED;
    setRegErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleRegSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setRegErrors((prev) => ({ ...prev, general: undefined }));
    if (!validateRegForm()) return;
    try {
      await register({
        ...regForm,
        email: regForm.email.trim(),
        name: regForm.name.trim(),
        phone: regForm.phone.trim(),
        specialization: regForm.specialization?.trim(),
        licenseNumber: regForm.licenseNumber?.trim(),
        role: getRegistrationRole(),
      });
    } catch (error) {
      setRegErrors((prev) => ({
        ...prev,
        general: error instanceof Error ? error.message : ERROR_MESSAGES.REGISTRATION_FAILED,
      }));
    }
  };

  // ============================================
  // GOOGLE OAUTH
  // ============================================

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) return;
    try {
      const result = await googleLogin(response.credential, intentRole);
      if (result.needsProfileCompletion) {
        setGoogleTempUserId(result.tempUserId || result.user.id);
        setGoogleUserName(result.user.name);
        setGoogleUserEmail(result.user.email);
        setAuthView(AuthView.COMPLETE_PROFILE);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : ERROR_MESSAGES.GOOGLE_AUTH_FAILED;
      if (authView === AuthView.SIGN_IN) setLoginErrors({ general: msg });
      else setRegErrors({ general: msg });
    }
  };

  const handleGoogleError = () => {
    const msg = ERROR_MESSAGES.GOOGLE_AUTH_FAILED;
    if (authView === AuthView.SIGN_IN) setLoginErrors({ general: msg });
    else setRegErrors({ general: msg });
  };

  // ============================================
  // PROFILE COMPLETION
  // ============================================

  const validateProfileForm = (): boolean => {
    const errs: RegistrationErrors = {};
    const role = intentRole || 'DOCTOR';
    if (role === 'DOCTOR') {
      if (!profileForm.specialization) errs.specialization = ERROR_MESSAGES.SPECIALIZATION_REQUIRED;
      if (!profileForm.licenseNumber || profileForm.licenseNumber.length < REGISTRATION_CONFIG.LICENSE_MIN_LENGTH) errs.licenseNumber = ERROR_MESSAGES.LICENSE_REQUIRED;
    }
    setProfileErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleProfileSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setProfileErrors((prev) => ({ ...prev, general: undefined }));
    if (!validateProfileForm()) return;
    const role = intentRole || 'DOCTOR';
    try {
      await completeProfile({
        userId: googleTempUserId,
        role,
        specialization: role === 'DOCTOR' ? profileForm.specialization : undefined,
        licenseNumber: role === 'DOCTOR' ? profileForm.licenseNumber : undefined,
        gender: profileForm.gender ? genderToApi(profileForm.gender) : undefined,
        phone: profileForm.phone || undefined,
      });
    } catch (error) {
      setProfileErrors((prev) => ({
        ...prev,
        general: error instanceof Error ? error.message : ERROR_MESSAGES.PROFILE_COMPLETION_FAILED,
      }));
    }
  };

  // ============================================
  // SHARED RENDER HELPERS
  // ============================================

  const hasGoogleAuth = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const renderGoogleDivider = () => {
    if (!hasGoogleAuth) return null;
    return (
      <div className={styles.dividerContainer}>
        <div className={styles.dividerLine} />
        <span className={styles.dividerText}>{STRING_CONSTANTS.LABELS.OR_CONTINUE_WITH}</span>
        <div className={styles.dividerLine} />
      </div>
    );
  };

  const renderGoogleButton = () => {
    if (!hasGoogleAuth) return null;
    return (
      <div className={styles.googleButtonWrapper} ref={googleBtnRef}>
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          width={String(googleBtnWidth)}
          text="continue_with"
          shape="rectangular"
          size="large"
          theme={isDark ? 'filled_black' : 'outline'}
          logo_alignment="left"
        />
      </div>
    );
  };

  const renderFieldError = (error: string | undefined, id: string) =>
    error ? <span id={id} className={styles.errorMessage} role="alert">{error}</span> : null;

  // ============================================
  // SIGN IN VIEW
  // ============================================

  const renderSignIn = () => (
    <>
      <div className={styles.formHeader}>
        <h1 className={styles.formTitle}>{STRING_CONSTANTS.LABELS.WELCOME_BACK}</h1>
        <p className={styles.formSubtitle}>{STRING_CONSTANTS.LABELS.LOGIN_TO_CONTINUE}</p>
      </div>

      <form onSubmit={handleLoginSubmit} className={styles.loginForm} noValidate>
        {loginErrors.general && <div className={styles.errorAlert} role="alert">{loginErrors.general}</div>}

        <div className={styles.formGroup}>
          <label htmlFor="email" className={styles.label}>{STRING_CONSTANTS.LABELS.EMAIL_ADDRESS}</label>
          <input id="email" type={FormFieldType.EMAIL} value={email}
            onChange={(e) => { setEmail(e.target.value); if (loginErrors.email) setLoginErrors((p) => ({ ...p, email: undefined })); }}
            className={loginErrors.email ? `${styles.input} ${styles.inputError}` : styles.input}
            placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_EMAIL} autoComplete="email"
            aria-invalid={!!loginErrors.email} disabled={isLoading} />
          {renderFieldError(loginErrors.email, 'email-error')}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="password" className={styles.label}>{STRING_CONSTANTS.LABELS.PASSWORD}</label>
          <div className={styles.passwordInputWrapper}>
            <input id="password" type={showPassword ? FormFieldType.TEXT : FormFieldType.PASSWORD}
              value={password} onChange={(e) => { setPassword(e.target.value); if (loginErrors.password) setLoginErrors((p) => ({ ...p, password: undefined })); }}
              className={loginErrors.password ? `${styles.passwordInput} ${styles.inputError}` : styles.passwordInput}
              placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_PASSWORD} autoComplete="current-password"
              aria-invalid={!!loginErrors.password} disabled={isLoading} />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className={styles.passwordToggle} aria-label={STRING_CONSTANTS.BUTTONS.TOGGLE_PASSWORD_VISIBILITY} disabled={isLoading}>
              <span className={`${styles.materialIcon} ${styles.passwordToggleMaterial}`} aria-hidden="true">{showPassword ? 'visibility' : 'visibility_off'}</span>
            </button>
          </div>
          {renderFieldError(loginErrors.password, 'password-error')}
        </div>

        <div className={styles.formOptions}>
          <div className={styles.rememberMeWrapper}>
            <input id="remember-me" type="checkbox" checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)} className={styles.checkbox} disabled={isLoading} />
            <label htmlFor="remember-me" className={styles.checkboxLabel}>{STRING_CONSTANTS.LABELS.REMEMBER_ME}</label>
          </div>
          <button type="button" className={styles.forgotPasswordLink} disabled={isLoading}
            onClick={() => window.alert(STRING_CONSTANTS.MESSAGES.FEATURE_COMING_SOON)}>
            {STRING_CONSTANTS.BUTTONS.FORGOT_PASSWORD}
          </button>
        </div>

        <button type="submit" className={styles.submitButton} disabled={isLoading} aria-busy={isLoading}>
          <span className={`${styles.materialIcon} ${styles.submitButtonMaterial}`} aria-hidden="true">lock</span>
          {isLoading ? STRING_CONSTANTS.MESSAGES.LOADING : STRING_CONSTANTS.BUTTONS.LOGIN}
        </button>

        {renderGoogleDivider()}
        {renderGoogleButton()}
      </form>

      <div className={styles.authToggle}>
        <span className={styles.authToggleText}>{STRING_CONSTANTS.LABELS.DONT_HAVE_ACCOUNT}</span>
        <button type="button" className={styles.authToggleLink}
          onClick={() => { setAuthView(AuthView.REGISTER); setLoginErrors({}); }}>
          {STRING_CONSTANTS.BUTTONS.REGISTER}
        </button>
      </div>
    </>
  );

  // ============================================
  // REGISTER VIEW
  // ============================================

  const renderRegister = () => (
    <>
      <div className={styles.formHeader}>
        <h1 className={styles.formTitle}>{STRING_CONSTANTS.LABELS.CREATE_ACCOUNT}</h1>
        <p className={styles.formSubtitle}>{getRegistrationRole() === 'PATIENT' ? STRING_CONSTANTS.LABELS.REGISTER_PATIENT_SUBTITLE : STRING_CONSTANTS.LABELS.REGISTER_DOCTOR_SUBTITLE}</p>
      </div>

      <form onSubmit={handleRegSubmit} className={styles.loginForm} noValidate>
        {regErrors.general && <div className={styles.errorAlert} role="alert">{regErrors.general}</div>}

        <p className={styles.sectionLabel}>{STRING_CONSTANTS.LABELS.ACCOUNT_SECTION}</p>

        <div className={styles.formGroup}>
          <label htmlFor="reg-name" className={styles.label}>{STRING_CONSTANTS.LABELS.FULL_NAME}</label>
          <input id="reg-name" type={FormFieldType.TEXT} value={regForm.name}
            onChange={(e) => updateRegField('name', e.target.value)}
            className={regErrors.name ? `${styles.input} ${styles.inputError}` : styles.input}
            placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_FULL_NAME} disabled={isLoading} />
          {renderFieldError(regErrors.name, 'reg-name-error')}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="reg-email" className={styles.label}>{STRING_CONSTANTS.LABELS.EMAIL_ADDRESS}</label>
          <input id="reg-email" type={FormFieldType.EMAIL} value={regForm.email}
            onChange={(e) => updateRegField('email', e.target.value)}
            className={regErrors.email ? `${styles.input} ${styles.inputError}` : styles.input}
            placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_EMAIL} autoComplete="email" disabled={isLoading} />
          {renderFieldError(regErrors.email, 'reg-email-error')}
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="reg-password" className={styles.label}>{STRING_CONSTANTS.LABELS.PASSWORD}</label>
            <div className={styles.passwordInputWrapper}>
              <input id="reg-password" type={showRegPassword ? FormFieldType.TEXT : FormFieldType.PASSWORD}
                value={regForm.password} onChange={(e) => updateRegField('password', e.target.value)}
                className={regErrors.password ? `${styles.passwordInput} ${styles.inputError}` : styles.passwordInput}
                placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_PASSWORD} autoComplete="new-password" disabled={isLoading} />
              <button type="button" onClick={() => setShowRegPassword(!showRegPassword)}
                className={styles.passwordToggle} aria-label={STRING_CONSTANTS.BUTTONS.TOGGLE_PASSWORD_VISIBILITY}>
                <span className={`${styles.materialIcon} ${styles.passwordToggleMaterial}`} aria-hidden="true">{showRegPassword ? 'visibility' : 'visibility_off'}</span>
              </button>
            </div>
            {renderFieldError(regErrors.password, 'reg-password-error')}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="reg-confirm" className={styles.label}>{STRING_CONSTANTS.LABELS.CONFIRM_PASSWORD}</label>
            <input id="reg-confirm" type={FormFieldType.PASSWORD} value={regForm.confirmPassword}
              onChange={(e) => updateRegField('confirmPassword', e.target.value)}
              className={regErrors.confirmPassword ? `${styles.input} ${styles.inputError}` : styles.input}
              placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_CONFIRM_PASSWORD} autoComplete="new-password" disabled={isLoading} />
            {renderFieldError(regErrors.confirmPassword, 'reg-confirm-error')}
          </div>
        </div>

        <p className={styles.sectionLabel}>{STRING_CONSTANTS.LABELS.PERSONAL_SECTION}</p>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="reg-gender" className={styles.label}>{STRING_CONSTANTS.LABELS.GENDER}</label>
            <select id="reg-gender" value={regForm.gender}
              onChange={(e) => updateRegField('gender', e.target.value)}
              className={`${styles.select} ${regErrors.gender ? styles.inputError : ''}`} disabled={isLoading}>
              <option value="">{STRING_CONSTANTS.PLACEHOLDERS.SELECT_GENDER}</option>
              <option value={Gender.MALE}>{Gender.MALE}</option>
              <option value={Gender.FEMALE}>{Gender.FEMALE}</option>
              <option value={Gender.OTHER}>{Gender.OTHER}</option>
            </select>
            {renderFieldError(regErrors.gender, 'reg-gender-error')}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="reg-phone" className={styles.label}>{STRING_CONSTANTS.LABELS.PHONE_NUMBER}</label>
            <input id="reg-phone" type={FormFieldType.TEXT} value={regForm.phone}
              onChange={(e) => updateRegField('phone', e.target.value)}
              className={styles.input} placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_PHONE} disabled={isLoading} />
          </div>
        </div>

        {getRegistrationRole() === 'DOCTOR' && (
          <>
            <p className={styles.sectionLabel}>{STRING_CONSTANTS.LABELS.PROFESSIONAL_SECTION}</p>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="reg-specialization" className={styles.label}>{STRING_CONSTANTS.LABELS.SPECIALIZATION}</label>
                <select id="reg-specialization" value={regForm.specialization}
                  onChange={(e) => updateRegField('specialization', e.target.value)}
                  className={`${styles.select} ${regErrors.specialization ? styles.inputError : ''}`} disabled={isLoading}>
                  <option value="">{STRING_CONSTANTS.PLACEHOLDERS.ENTER_SPECIALIZATION}</option>
                  {REGISTRATION_CONFIG.SPECIALIZATIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {renderFieldError(regErrors.specialization, 'reg-spec-error')}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="reg-license" className={styles.label}>{STRING_CONSTANTS.LABELS.LICENSE_NUMBER}</label>
                <input id="reg-license" type={FormFieldType.TEXT} value={regForm.licenseNumber}
                  onChange={(e) => updateRegField('licenseNumber', e.target.value)}
                  className={regErrors.licenseNumber ? `${styles.input} ${styles.inputError}` : styles.input}
                  placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_LICENSE_NUMBER} disabled={isLoading} />
                {renderFieldError(regErrors.licenseNumber, 'reg-license-error')}
              </div>
            </div>
          </>
        )}

        <button type="submit" className={styles.submitButton} disabled={isLoading} aria-busy={isLoading}>
          <span className={`${styles.materialIcon} ${styles.submitButtonMaterial}`} aria-hidden="true">person_add</span>
          {isLoading ? STRING_CONSTANTS.MESSAGES.LOADING : STRING_CONSTANTS.BUTTONS.REGISTER}
        </button>

        {renderGoogleDivider()}
        {renderGoogleButton()}
      </form>

      <div className={styles.authToggle}>
        <span className={styles.authToggleText}>{STRING_CONSTANTS.LABELS.ALREADY_HAVE_ACCOUNT}</span>
        <button type="button" className={styles.authToggleLink}
          onClick={() => { setAuthView(AuthView.SIGN_IN); setRegErrors({}); }}>
          {STRING_CONSTANTS.LABELS.SIGN_IN}
        </button>
      </div>
    </>
  );

  // ============================================
  // COMPLETE PROFILE VIEW (Google OAuth)
  // ============================================

  const renderCompleteProfile = () => {
    const role = intentRole || 'DOCTOR';
    const isPatient = role === 'PATIENT';
    return (
    <>
      <div className={styles.formHeader}>
        <h1 className={styles.formTitle}>{STRING_CONSTANTS.LABELS.COMPLETE_YOUR_PROFILE}</h1>
        <p className={styles.formSubtitle}>{
          isPatient
            ? STRING_CONSTANTS.LABELS.COMPLETE_PROFILE_SUBTITLE_PATIENT
            : STRING_CONSTANTS.LABELS.COMPLETE_PROFILE_SUBTITLE
        }</p>
      </div>

      <form onSubmit={handleProfileSubmit} className={styles.loginForm} noValidate>
        {profileErrors.general && <div className={styles.errorAlert} role="alert">{profileErrors.general}</div>}

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>{STRING_CONSTANTS.LABELS.FULL_NAME}</label>
            <input type={FormFieldType.TEXT} value={googleUserName}
              className={`${styles.input} ${styles.readOnlyField}`} readOnly />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{STRING_CONSTANTS.LABELS.EMAIL_ADDRESS}</label>
            <input type={FormFieldType.EMAIL} value={googleUserEmail}
              className={`${styles.input} ${styles.readOnlyField}`} readOnly />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="profile-gender" className={styles.label}>{STRING_CONSTANTS.LABELS.GENDER}</label>
            <select id="profile-gender" value={profileForm.gender}
              onChange={(e) => setProfileForm((p) => ({ ...p, gender: e.target.value as Gender }))}
              className={styles.select} disabled={isLoading}>
              <option value="">{STRING_CONSTANTS.PLACEHOLDERS.SELECT_GENDER}</option>
              <option value={Gender.MALE}>{Gender.MALE}</option>
              <option value={Gender.FEMALE}>{Gender.FEMALE}</option>
              <option value={Gender.OTHER}>{Gender.OTHER}</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="profile-phone" className={styles.label}>{STRING_CONSTANTS.LABELS.PHONE_NUMBER}</label>
            <input id="profile-phone" type={FormFieldType.TEXT} value={profileForm.phone}
              onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))}
              className={styles.input} placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_PHONE} disabled={isLoading} />
          </div>
        </div>

        {!isPatient && (
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="profile-spec" className={styles.label}>{STRING_CONSTANTS.LABELS.SPECIALIZATION}</label>
              <select id="profile-spec" value={profileForm.specialization}
                onChange={(e) => { setProfileForm((p) => ({ ...p, specialization: e.target.value })); if (profileErrors.specialization) setProfileErrors((p) => ({ ...p, specialization: undefined })); }}
                className={`${styles.select} ${profileErrors.specialization ? styles.inputError : ''}`} disabled={isLoading}>
                <option value="">{STRING_CONSTANTS.PLACEHOLDERS.ENTER_SPECIALIZATION}</option>
                {REGISTRATION_CONFIG.SPECIALIZATIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {renderFieldError(profileErrors.specialization, 'profile-spec-error')}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="profile-license" className={styles.label}>{STRING_CONSTANTS.LABELS.LICENSE_NUMBER}</label>
              <input id="profile-license" type={FormFieldType.TEXT} value={profileForm.licenseNumber}
                onChange={(e) => { setProfileForm((p) => ({ ...p, licenseNumber: e.target.value })); if (profileErrors.licenseNumber) setProfileErrors((p) => ({ ...p, licenseNumber: undefined })); }}
                className={profileErrors.licenseNumber ? `${styles.input} ${styles.inputError}` : styles.input}
                placeholder={STRING_CONSTANTS.PLACEHOLDERS.ENTER_LICENSE_NUMBER} disabled={isLoading} />
              {renderFieldError(profileErrors.licenseNumber, 'profile-license-error')}
            </div>
          </div>
        )}

        <button type="submit" className={styles.submitButton} disabled={isLoading} aria-busy={isLoading}>
          <span className={`${styles.materialIcon} ${styles.submitButtonMaterial}`} aria-hidden="true">check_circle</span>
          {isLoading ? STRING_CONSTANTS.MESSAGES.LOADING : STRING_CONSTANTS.BUTTONS.COMPLETE_PROFILE}
        </button>
      </form>

      <div className={styles.authToggle}>
        <button type="button" className={styles.authToggleLink}
          onClick={() => { setAuthView(AuthView.SIGN_IN); setProfileErrors({}); }}>
          {STRING_CONSTANTS.BUTTONS.BACK_TO_SIGN_IN}
        </button>
      </div>
    </>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginContent}>
        {/* Left Column - Branding */}
        <div className={styles.leftPanel}>
          <div className={styles.brandingContent}>
            <div className={styles.logoIconLarge}>
              <span className={`${styles.materialIcon} ${styles.logoIconLargeMaterial}`} aria-hidden="true">health_and_safety</span>
            </div>
            <h2 className={styles.brandName}>{STRING_CONSTANTS.LABELS.APP_NAME}</h2>
          </div>
        </div>

        {/* Right Column - Form */}
        <div className={styles.rightPanel}>
          <div className={styles.formContainer}>
            {/* Back to landing (only when callback is provided) */}
            {onBackToLanding && (
              <button
                type="button"
                className={styles.backToLanding}
                onClick={onBackToLanding}
                aria-label={LANDING_STRINGS.ARIA.BACK_TO_HOME}
              >
                <span className={`${styles.materialIcon} ${styles.backToLandingIcon}`} aria-hidden="true">arrow_back</span>
                {LANDING_STRINGS.NAV.BRAND}
              </button>
            )}

            {/* Mobile Logo */}
            <div className={styles.mobileHeader}>
              <div className={styles.mobileLogo}>
                <div className={styles.logoIconSmall}>
                  <span className={`${styles.materialIcon} ${styles.logoIconSmallMaterial}`} aria-hidden="true">health_and_safety</span>
                </div>
                <h2 className={styles.mobileAppName}>{STRING_CONSTANTS.LABELS.APP_NAME}</h2>
              </div>
            </div>

            {authView === AuthView.SIGN_IN && renderSignIn()}
            {authView === AuthView.REGISTER && renderRegister()}
            {authView === AuthView.COMPLETE_PROFILE && renderCompleteProfile()}

            {/* Footer */}
            <div className={styles.footer}>
              <p className={styles.copyright}>{STRING_CONSTANTS.LABELS.COPYRIGHT}</p>
              <div className={styles.footerLinks}>
                <button type="button" className={styles.footerLink} onClick={() => window.alert(STRING_CONSTANTS.MESSAGES.FEATURE_COMING_SOON)}>{STRING_CONSTANTS.LABELS.HELP}</button>
                <span className={styles.footerSeparator}>&middot;</span>
                <button type="button" className={styles.footerLink} onClick={() => window.alert(STRING_CONSTANTS.MESSAGES.FEATURE_COMING_SOON)}>{STRING_CONSTANTS.LABELS.PRIVACY}</button>
                <span className={styles.footerSeparator}>&middot;</span>
                <button type="button" className={styles.footerLink} onClick={() => window.alert(STRING_CONSTANTS.MESSAGES.FEATURE_COMING_SOON)}>{STRING_CONSTANTS.LABELS.TERMS}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
